<script>
import axios from 'axios';

export default {
    name: 'PartnerReportGenerateBar',
    props: {
        canGenerate: {
            type: Boolean,
            default: false,
        },
        generateUrl: {
            type: String,
            default: '',
        },
        defaultGenerateMonth: {
            type: String,
            default: '',
        },
        exportsIndexUrl: {
            type: String,
            default: '',
        },
        exportStatusUrlTemplate: {
            type: String,
            default: '',
        },
        kpiLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            generateMonth: '',
            generating: false,
            lastExportId: null,
            queuedMessage: '',
        };
    },
    computed: {
        showGenerateAction() {
            return this.canGenerate && this.generateUrl !== '';
        },
        isBusy() {
            return this.generating || this.kpiLoading;
        },
        canSubmitGenerate() {
            return this.showGenerateAction && this.generateMonth !== '' && !this.isBusy;
        },
    },
    watch: {
        defaultGenerateMonth: {
            immediate: true,
            handler(value) {
                const normalized = this.normalizeYearMonth(value);
                if (normalized !== '') {
                    this.generateMonth = normalized;
                }
            },
        },
    },
    mounted() {
        if (this.generateMonth === '') {
            this.generateMonth = this.normalizeYearMonth(this.defaultGenerateMonth)
                || this.currentYearMonth();
        }
    },
    methods: {
        normalizeYearMonth(value) {
            if (typeof value !== 'string' || value === '') {
                return '';
            }

            if (/^\d{4}-\d{2}$/.test(value)) {
                return value;
            }

            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return value.slice(0, 7);
            }

            return '';
        },
        currentYearMonth() {
            const now = new Date();
            const month = String(now.getMonth() + 1).padStart(2, '0');

            return `${now.getFullYear()}-${month}`;
        },
        async generateReport() {
            if (!this.canSubmitGenerate) {
                return;
            }

            this.generating = true;
            this.queuedMessage = '';

            try {
                const response = await axios.post(this.generateUrl, {
                    from: this.generateMonth,
                    to: this.generateMonth,
                });

                this.lastExportId = response.data?.export_id ?? null;
                this.queuedMessage = response.data?.message
                    ?? this._t('Export queued. The dashboard refreshes when materialization completes.');
            } catch (error) {
                const message = error?.response?.data?.message ?? this._t('Unable to queue export.');

                if (window.Swal) {
                    await window.Swal.fire({ icon: 'error', text: message });
                }
            } finally {
                this.generating = false;
            }
        },
    },
};
</script>

<template>
    <div v-if="showGenerateAction" class="partner-report-generate-bar">
        <div class="partner-report-generate-bar__controls">
            <label class="partner-report-generate-bar__label" for="partner-report-generate-month">
                {{ _t('Month to generate') }}
            </label>
            <input
                id="partner-report-generate-month"
                v-model="generateMonth"
                type="month"
                class="form-control input-sm partner-report-generate-bar__month"
                :disabled="isBusy"
            />
            <button
                type="button"
                class="btn btn-primary btn-sm partner-report-generate-bar__submit"
                :disabled="!canSubmitGenerate"
                @click="generateReport"
            >
                {{ generating ? _t('Generating...') : _t('Generate CSV') }}
            </button>
        </div>
        <p class="help-block partner-report-generate-bar__hint">
            {{ _t('Materializes aggregate CSV (and detail when configured) for the selected month only.') }}
        </p>
        <p v-if="lastExportId || queuedMessage" class="help-block partner-report-generate-bar__status">
            <template v-if="lastExportId">
                {{ _t('Export queued') }} #{{ lastExportId }}.
            </template>
            {{ queuedMessage }}
            <a v-if="exportsIndexUrl" :href="exportsIndexUrl">{{ _t('View exports') }}</a>
        </p>
    </div>
</template>

<style scoped>
.partner-report-generate-bar {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #f4f4f4;
}

.partner-report-generate-bar__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
}

.partner-report-generate-bar__label {
    margin: 0;
    font-weight: 600;
}

.partner-report-generate-bar__month {
    width: auto;
    min-width: 10rem;
}

.partner-report-generate-bar__hint {
    margin-top: 8px;
    margin-bottom: 0;
}

.partner-report-generate-bar__status {
    margin-top: 8px;
}
</style>
